import * as React from 'react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  HelperText,
  HelperTextItem,
  NumberInput,
  Stack,
  StackItem,
  TextArea,
  TextInput,
  Title,
} from '@patternfly/react-core';

import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

interface EodBudgetStatus {
  spend: number;
  limit: number;
  exceeded: boolean;
}

const usd = (n: number) => `$${n.toFixed(2)}`;

const EodBudgetAdminPage: React.FunctionComponent = () => {
  const getToken = useAccessToken();
  const [team, setTeam] = useState('');
  const [status, setStatus] = useState<EodBudgetStatus | null>(null);
  const [limitUsd, setLimitUsd] = useState<number>(1200);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const teamPath = () => encodeURIComponent(team.trim());

  const lookup = async () => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const s = await fetchApi<EodBudgetStatus>(`/api/eod-budget/${teamPath()}`, getToken);
      setStatus(s);
      setLimitUsd(s.limit);
    } catch (e) {
      setStatus(null);
      setError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await fetchApi(`/api/eod-budget/${teamPath()}/override`, getToken, {
        method: 'PUT',
        body: JSON.stringify({ limitUsd, reason: reason.trim() }),
      });
      setSuccess(`Limit for ${team.trim()} set to ${usd(limitUsd)} for this month. It reverts to the default next month.`);
      setReason('');
      const s = await fetchApi<EodBudgetStatus>(`/api/eod-budget/${teamPath()}`, getToken);
      setStatus(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Saving failed');
    } finally {
      setBusy(false);
    }
  };

  const canSave = team.trim() !== '' && reason.trim() !== '' && Number.isFinite(limitUsd) && limitUsd >= 0 && !busy;

  return (
    <Stack hasGutter>
      <StackItem>
        <Title headingLevel="h1">EOD Budget Exceptions</Title>
        <p>Raise a team&apos;s monthly EOD compute limit after an approved exception. Applies to the current calendar month only and is audit-logged.</p>
      </StackItem>
      {error && (
        <StackItem>
          <Alert variant="danger" isInline title={error} />
        </StackItem>
      )}
      {success && (
        <StackItem>
          <Alert variant="success" isInline title={success} />
        </StackItem>
      )}
      <StackItem>
        <Form isWidthLimited>
          <FormGroup label="Team" isRequired fieldId="eod-budget-team">
            <TextInput id="eod-budget-team" value={team} onChange={(_e, v) => setTeam(v)} />
          </FormGroup>
          <div>
            <Button variant="secondary" onClick={lookup} isDisabled={team.trim() === '' || busy}>
              Look up current spend
            </Button>
          </div>
          {status && (
            <Alert
              variant={status.exceeded ? 'danger' : 'info'}
              isInline
              title={`Spend ${usd(status.spend)} of ${usd(status.limit)} limit${status.exceeded ? ' (over limit)' : ''}`}
            />
          )}
          <FormGroup label="New limit for this month (USD)" isRequired fieldId="eod-budget-limit">
            <NumberInput
              value={limitUsd}
              min={0}
              step={100}
              onMinus={() => setLimitUsd((v) => Math.max(0, (Number(v) || 0) - 100))}
              onPlus={() => setLimitUsd((v) => (Number(v) || 0) + 100)}
              onChange={(e) => setLimitUsd(Number((e.target as HTMLInputElement).value))}
              inputName="eod-budget-limit"
              inputAriaLabel="New limit in USD"
              minusBtnAriaLabel="Decrease limit"
              plusBtnAriaLabel="Increase limit"
            />
          </FormGroup>
          <FormGroup label="Reason / exception reference" isRequired fieldId="eod-budget-reason">
            <TextArea id="eod-budget-reason" value={reason} onChange={(_e, v) => setReason(v)} />
            <HelperText>
              <HelperTextItem>Recorded in the audit log, e.g. the approved exception ticket.</HelperTextItem>
            </HelperText>
          </FormGroup>
          <div>
            <Button variant="primary" onClick={save} isDisabled={!canSave}>
              Raise limit
            </Button>
          </div>
        </Form>
      </StackItem>
    </Stack>
  );
};

export { EodBudgetAdminPage };
